import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/hooks/useAuth';

import {
  getFavorites,
  addFavorite,
  removeFavorite,
  checkFavorite,
  getAdminUserFavorites,
  getAdminProfessionalFavorites,
} from '../services/favorite.service';

export function useFavorites() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['favorites'],
    queryFn: getFavorites,
    enabled: isAuthenticated,
    staleTime: 1000 * 60,
  });
}

export function useIsFavorite(professionalId: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['favorite-check', professionalId],
    queryFn: () => checkFavorite(professionalId!),
    enabled: isAuthenticated && !!professionalId,
    staleTime: 1000 * 60,
  });
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();

  const add = useMutation({
    mutationFn: addFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      queryClient.invalidateQueries({ queryKey: ['favorite-check'] });
    },
  });

  const remove = useMutation({
    mutationFn: removeFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      queryClient.invalidateQueries({ queryKey: ['favorite-check'] });
    },
  });

  return { add, remove };
}

/** Scheda admin di un cliente: quali consulenti ha tra i preferiti. */
export function useAdminUserFavorites(userId: string | undefined) {
  return useQuery({
    queryKey: ['admin-user-favorites', userId],
    queryFn: () => getAdminUserFavorites(userId!),
    enabled: !!userId,
  });
}

/** Scheda admin di un consulente: da quali clienti è stato salvato. */
export function useAdminProfessionalFavorites(professionalId: string | undefined) {
  return useQuery({
    queryKey: ['admin-professional-favorites', professionalId],
    queryFn: () => getAdminProfessionalFavorites(professionalId!),
    enabled: !!professionalId,
  });
}
